import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PlanCards, type BillingPeriod } from '@/features/pricing/PlanCards';
import { CURRENCIES, type CurrencyCode } from '@/lib/pricing';
import { annualSavingPct } from '@/lib/pricing';
import { useTier } from '@/session/SessionProvider';
import './Pricing.css';

/**
 * Pricing — the in-app upgrade surface.
 *
 * Three self-serve cards in one row, plus a Team banner **below, not as a fourth card**.
 * Team is a "Talk to Sales" callout with a Close.com webform, not a self-serve tier.
 *
 * The currency selector drives Snapshot's availability: it is sold in the US and Canada only,
 * so in any other currency its card goes to an explicit "unavailable in this currency" state
 * with a route to Intelligence, rather than disappearing or showing a converted price.
 *
 * The saving shown on the yearly toggle is measured from the two price points rather than
 * hardcoded, so the badge can never claim a discount the customer does not receive.
 */
export function Pricing() {
  const tier = useTier();
  const navigate = useNavigate();
  // `?currency=GBP` preselects — handy for support and for checking the states where a plan
  // is not sold. Falls back to USD for anything unrecognized.
  const [currency, setCurrency] = useState<CurrencyCode>(() => {
    const q = new URLSearchParams(window.location.search).get('currency');
    return q && q in CURRENCIES ? (q as CurrencyCode) : 'USD';
  });
  const [period, setPeriod] = useState<BillingPeriod>('monthly');

  // Every currency lands on the same prepay discount, so quoting Intelligence's is honest.
  const savingPct = annualSavingPct(currency, 'intelligence');

  return (
    <div className="pricing">
      <header className="pricing__head">
        <h1 className="pricing__title">Plans</h1>
        <p className="pricing__subtitle">
          Every plan includes a 14-day free trial. Change or cancel whenever you like.
        </p>
      </header>

      <div className="pricing__controls">
        <div className="pricing__toggle" role="tablist" aria-label="Billing period">
          <button
            type="button"
            role="tab"
            aria-selected={period === 'monthly'}
            data-active={period === 'monthly'}
            onClick={() => setPeriod('monthly')}
          >
            Monthly
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={period === 'annual'}
            data-active={period === 'annual'}
            onClick={() => setPeriod('annual')}
          >
            Pay yearly <span className="pricing__save">Save {savingPct}%</span>
          </button>
        </div>

        <label className="pricing__currency">
          <span className="u-sr-only">Currency</span>
          <select value={currency} onChange={(e) => setCurrency(e.target.value as CurrencyCode)}>
            {Object.values(CURRENCIES).map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.code}
              </option>
            ))}
          </select>
        </label>
      </div>

      <PlanCards
        currency={currency}
        period={period}
        currentTier={tier}
        onChoose={() => void navigate({ to: '/account/change-plan' })}
      />

      {/* A banner, deliberately not a fourth card — an empty grid cell was a real bug once. */}
      <section className="pricing__team">
        <div>
          <h2 className="pricing__teamTitle">Rolling this out to a team?</h2>
          <p className="pricing__teamBody">
            Ten seats or more comes with volume pricing and a single invoice. Tell us what you’re
            tracking and we’ll size it with you.
          </p>
        </div>
        <a className="pricing__teamCta" href="#talk-to-sales">
          Talk to Sales
        </a>
      </section>

      <p className="pricing__footnote">
        Prices in {CURRENCIES[currency].label}. Aware Explain and Ask Aware top-up packs are a
        one-time purchase, never expire, and carry over if you change plan.
      </p>
    </div>
  );
}
